import React, { useEffect, useRef } from "react";
import styled from "@emotion/styled";
import PropTypes from "prop-types";
import Freezeframe from "freezeframe";

const Slide = ({ slide, handleClick, index }) => {
	const imageRef = useRef(null);
	const freezeframeRef = useRef(null);
	const isGif = slide.url.toLowerCase().endsWith(".gif");

	const startGif = () => {
		if (freezeframeRef.current) {
			imageRef.current.style.visibility = "";
			freezeframeRef.current.start();
		}
	};

	const stopGif = () => {
		if (freezeframeRef.current) {
			freezeframeRef.current.stop();
		}
	};

	useEffect(() => {
		if (!imageRef.current || !isGif) {
			return undefined;
		}

		const image = imageRef.current;

		const initializeFreezeframe = () => {
			if (freezeframeRef.current) {
				freezeframeRef.current.destroy();
			}

			image.style.visibility = "hidden";

			freezeframeRef.current = new Freezeframe(image, {
				trigger: false,
				responsive: false,
			});
		};

		if (image.complete && image.naturalWidth > 0) {
			initializeFreezeframe();
		} else {
			image.addEventListener("load", initializeFreezeframe, { once: true });
		}

		return () => {
			image.removeEventListener("load", initializeFreezeframe);

			if (freezeframeRef.current) {
				freezeframeRef.current.destroy();
				freezeframeRef.current = null;
			}
		};
	}, [isGif, slide.url]);

	return (
		<Container
			type="button"
			onClick={() => handleClick(index)}
			onMouseEnter={startGif}
			onMouseLeave={stopGif}
			onFocus={startGif}
			onBlur={stopGif}
		>
			<ImageWrapper>
				<Image ref={imageRef} src={slide.url} alt={slide.alt} />
				{isGif && (
					<FrozenOverlay className="slide-frozen-overlay" aria-hidden="true" />
				)}
				{isGif && <PlayIcon className="slide-play-icon" aria-hidden="true" />}
			</ImageWrapper>
			{slide.caption && (
				<Caption dangerouslySetInnerHTML={{ __html: slide.caption }} />
			)}
		</Container>
	);
};

Slide.propTypes = {
	slide: PropTypes.object,
	handleClick: PropTypes.func,
	index: PropTypes.number,
};

export default Slide;

const Container = styled.button`
	width: min-content;
	padding: 0;
	border: 0;
	background: none;
	text-align: left;
	cursor: pointer;

	&:focus-visible {
		outline: 2px solid #0079c1;
		outline-offset: 2px;
		border-radius: 5px;
	}

	/* Remove FreezeFrame loading spinner */
	.ff-loading-icon::before {
		display: none;
	}

	/* Prevent layout shift when FreezeFrame wraps the image in a container div */
	.ff-container {
		display: block;
		height: 313px;
		width: auto;
		border-radius: 10px;
		overflow: hidden;
	}

	/* Smooth fade transition when playing/pausing */
	.ff-canvas,
	.ff-image {
		transition: opacity 400ms ease !important;
	}

	/* Hide play icon when GIF is playing */
	&:hover .slide-play-icon,
	&:focus .slide-play-icon {
		opacity: 0;
	}

	&:hover .slide-frozen-overlay,
	&:focus .slide-frozen-overlay {
		opacity: 0;
	}
`;

const ImageWrapper = styled.div`
	position: relative;
	border-radius: 10px;
	overflow: hidden;
`;

const FrozenOverlay = styled.div`
	position: absolute;
	inset: 0;
	background: rgba(0, 0, 0, 0.2);
	pointer-events: none;
	transition: opacity 400ms ease;
	z-index: 2;
`;

const PlayIcon = styled.div`
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	width: 75px;
	height: 75px;
	background-image: url(${mainScript.assetPath +
		"images/svg/icons/icon-play.svg"});
	background-repeat: no-repeat;
	background-size: contain;
	background-position: center;
	pointer-events: none;
	transition: opacity 400ms ease;
	z-index: 3;
`;


const Image = styled.img`
	height: 313px;
	width: auto;
	border-radius: 10px;
`;

const Caption = styled.p`
	font-size: 1.4rem;
	letter-spacing: 0.25px;
	line-height: 2.4rem;
`;
